/**
 * StepStore — first step of the Wix AI Shopping Assistant onboarding: bring in the product catalog.
 *
 * A site with Wix Stores syncs its catalog automatically (the durable store mirror, same as
 * AI Studio > Apps > Wix > Products). Without a store, or in addition, the owner uploads a product
 * file or imports a shop website through the Knowledge Base's own modals (Product Analyzer first).
 * Everything runs in the background, so the owner can continue while products are added.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping, faCheck, faFileArrowUp, faGlobe, faRotateRight, faTags, faTableCellsLarge, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import StepFooter from './StepFooter';
import UploadFileModal from '../../../components/uploadFileModal.js';
import ProductAnalyzerModal from '../../../components/ProductAnalyzerModal.js';
import AddProductWebsiteModal from '../../../components/AddProductWebsiteModal.js';
import { guardWrite } from '../../../utils/permissions';
import './StepStore.css';

const API = process.env.REACT_APP_API_BASE_URL;
const RUNNING = ['pending', 'scraping', 'processing'];
const POLL_MS = 4000;
const IMPORT_WATCH_MS = 3 * 60 * 1000;
const SKILLS = [
  { icon:faTags, key:'wix_store_skill_find' },
  { icon:faTableCellsLarge, key:'wix_store_skill_cards' },
  { icon:faScaleBalanced, key:'wix_store_skill_compare' },
];

export default function StepStore({ botId, goNext, busy: saving }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [websiteOpen, setWebsiteOpen] = useState(false);
  const [analyzer, setAnalyzer] = useState(null);   // { loading, catalogs, urls | website }
  const syncStarted = useRef(false);
  const manualBefore = useRef(0);

  const refresh = useCallback(async () => {
    const response = await fetch(`${API}products/sources?bot_id=${encodeURIComponent(botId)}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || t('wix_store_status_error'));
    setStatus(data);
    return data;
  }, [botId, t]);

  // A store that cannot be read (no Wix Stores, missing permission) shows the other ways to add products.
  const syncWix = useCallback(async () => {
    setError(''); setSyncFailed(false);
    const response = await fetch(`${API}products/sources/wix/sync?bot_id=${encodeURIComponent(botId)}`, { method:'POST' });
    if (!response.ok) { setSyncFailed(true); return; }
    await refresh();
  }, [botId, refresh]);

  // First visit: read the catalog state and start the Wix Stores sync once if it never ran.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await refresh();
        if (active && data.wix_connected && !data.counts?.wix && !syncStarted.current) {
          syncStarted.current = true;
          await syncWix();
        }
      } catch (e) { if (active) setError(e.message); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [refresh, syncWix]);

  const wix = status?.counts?.wix;
  const manual = status?.counts?.manual?.product_count || 0;
  const wixCount = wix?.product_count || 0;
  const wixSyncing = !!wix && RUNNING.includes(wix.status);
  const wixFailed = wix?.status === 'failed';
  const total = wixCount + manual;

  // While a sync or an import runs, follow it (the product count grows chunk by chunk).
  useEffect(() => {
    if (!wixSyncing && !importing) return undefined;
    const timer = setInterval(() => { refresh().catch(() => {}); }, POLL_MS);
    return () => clearInterval(timer);
  }, [wixSyncing, importing, refresh]);
  // An import is visible once its first products land; a large crawl keeps running in the Knowledge Base.
  useEffect(() => {
    if (!importing) return undefined;
    if (manual > manualBefore.current) { setImporting(false); return undefined; }
    const timer = setTimeout(() => setImporting(false), IMPORT_WATCH_MS);
    return () => clearTimeout(timer);
  }, [importing, manual]);

  async function analyze(path, body, draft) {
    if (!guardWrite()) return;
    setError('');
    setAnalyzer({ ...draft, loading:true, catalogs:null });
    try {
      const response = await fetch(`${API}${path}`, { method:'POST', headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({ bot_id:botId, ...body }) });
      if (response.status === 403) { setAnalyzer(null); return; }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('wix_store_import_error'));
      setAnalyzer({ ...draft, loading:false, catalogs:data.catalogs || [] });
    } catch (e) {
      // The analysis only drafts a schema: a file still imports without it, a website needs it.
      setAnalyzer(null);
      if (draft.urls) await ingest(draft, {}); else setError(e.message);
    }
  }

  async function ingest(draft, schemas) {
    setAnalyzer(null);
    if (!guardWrite()) return;
    const website = draft.website;
    const body = website
      ? { bot_id:botId, website_url:website, schema:(schemas && (schemas[website] || Object.values(schemas)[0])) || {} }
      : { bot_id:botId, product_urls:draft.urls, schemas:schemas || {} };
    try {
      const response = await fetch(`${API}${website ? 'ingest_product_website' : 'ingest_products'}`, { method:'POST',
        headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ ...body, user_id:localStorage.getItem('user_id') }) });
      if (response.status === 403) return;
      if (!response.ok) throw new Error(t('wix_store_import_error'));
      manualBefore.current = manual;
      setImporting(true);
    } catch (e) { setError(e.message); }
  }

  const wixState = !status?.wix_connected ? null
    : wixSyncing ? 'syncing' : wixCount > 0 ? 'ready' : wixFailed || syncFailed ? 'failed' : wix ? 'empty' : 'starting';
  return <>
    <header className="ac-head">
      <h2 className="ac-title ac-store-title"><span className="ac-store-title-icon" aria-hidden="true"><FontAwesomeIcon icon={faBagShopping} /></span>{t('wix_store_title')}</h2>
      <p className="ac-sub">{t('wix_store_sub')}</p>
    </header>
    <div className="ac-body ac-store-body">
      {loading ? <div className="ac-store-status" role="status"><span className="ac-spinner ac-spinner-sm" />{t('loading')}</div> : <>
        {wixState && <section className={`ac-store-source is-${wixState}`} aria-live="polite">
          <img className="ac-store-logo" src="/wix/stores.png" alt="" aria-hidden="true" />
          <div className="ac-store-copy">
            <h3>{t('wix_store_wix_title')}</h3>
            <p>{t(`wix_store_wix_${wixState}`, { count:wixCount })}</p>
            {wixSyncing && <div className="ac-strip-bar ac-store-bar"><span className="ac-strip-fill ac-store-fill" /></div>}
          </div>
          {wixState === 'ready' && <span className="ac-store-badge"><FontAwesomeIcon icon={faCheck} /> {t('wix_store_synced')}</span>}
          {(wixState === 'failed' || wixState === 'empty') && <button type="button" className="ac-btn-ghost" disabled={saving}
            onClick={() => syncWix().catch(e => setError(e.message))}><FontAwesomeIcon icon={faRotateRight} />{t('wix_store_sync_again')}</button>}
        </section>}
        <div className="ac-store-more">
          <p className="ac-store-more-label">{t(status?.wix_connected ? 'wix_store_more_title' : 'wix_store_add_title')}</p>
          <div className="ac-store-actions">
            <button type="button" className="ac-store-action" disabled={saving} onClick={() => setUploadOpen(true)}>
              <span className="ac-store-action-icon" aria-hidden="true"><FontAwesomeIcon icon={faFileArrowUp} /></span>
              <span className="ac-store-action-copy"><strong>{t('wix_store_upload_title')}</strong><span>{t('wix_store_upload_desc')}</span></span>
            </button>
            <button type="button" className="ac-store-action" disabled={saving} onClick={() => setWebsiteOpen(true)}>
              <span className="ac-store-action-icon" aria-hidden="true"><FontAwesomeIcon icon={faGlobe} /></span>
              <span className="ac-store-action-copy"><strong>{t('wix_store_website_title')}</strong><span>{t('wix_store_website_desc')}</span></span>
            </button>
          </div>
          {(importing || manual > 0) && <div className="ac-store-status" role="status">
            {importing ? <><span className="ac-spinner ac-spinner-sm" />{t('wix_store_importing')}</>
              : <><FontAwesomeIcon icon={faCheck} className="ac-store-ok" />{t('wix_store_imported', { count:manual })}</>}
          </div>}
        </div>
        <ul className="ac-store-skills" aria-label={t('wix_store_skills_label')}>
          {SKILLS.map(item => <li key={item.key}><span aria-hidden="true"><FontAwesomeIcon icon={item.icon} /></span>{t(item.key)}</li>)}
        </ul>
      </>}
      {error && <div className="ac-error" role="alert">{error}</div>}
    </div>
    <StepFooter primaryLabel={t('wxo_continue')} onPrimary={goNext}
      onSkip={!total && !wixSyncing && !importing ? goNext : undefined} skipLabel={t('wix_store_later')}
      disabled={loading || (!total && !wixSyncing && !importing)} busy={saving}
      hint={wixSyncing || importing ? t('wix_store_background') : null} />
    <UploadFileModal open={uploadOpen} onClose={() => setUploadOpen(false)}
      onSubmit={urls => analyze('analyze_products', { product_urls:urls }, { urls })}
      allowedExtensions={['csv', 'json', 'tsv', 'txt']} acceptAttr=".csv,.json,.tsv,.txt" fileTags={['.csv', '.json', '.tsv']} />
    <AddProductWebsiteModal open={websiteOpen} onClose={() => setWebsiteOpen(false)}
      onSubmit={url => { setWebsiteOpen(false); analyze('analyze_product_website', { website_url:url }, { website:url }); }} />
    <ProductAnalyzerModal open={!!analyzer} loading={!!analyzer?.loading} catalogs={analyzer?.catalogs}
      onClose={() => setAnalyzer(null)} onConfirm={schemas => ingest(analyzer, schemas)} />
  </>;
}
